'use client';

// Thin per-app wrapper around `<RealtimeStack>` — supplies admin-web's
// browser-side subscriber and mounts the shared three-provider stack. The
// composition itself lives in `@auctogon/core/realtime` so admin-web and
// bidder-web stay in sync (Rule 18 / no-cross-app-copy-paste).

import { useMemo, type ReactNode } from 'react';

import { RealtimeStack } from '@auctogon/core/realtime';

import ConnectionHealthBarMount from '@auctogon/admin-frontend/components/ui/ConnectionHealthBarMount';
import { ChangeQueueCountBridge } from '@auctogon/admin-frontend/components/offline/ChangeQueueCountBridge';
import { InboxCountBridge } from '@auctogon/admin-frontend/components/inbox/InboxCountBridge';
import {
    OfflineModeProvider,
    readOfflineDocumentFlag,
} from '@auctogon/admin-frontend/components/offline/OfflineModeProvider';
import { startsInManualOffline } from '@auctogon/admin-frontend/components/offline/manual-offline-flag';

import { getRealtimeSubscriber } from './get-realtime-subscriber';

interface Props {
    children: ReactNode;
}

export function RealtimeProviderMount({ children }: Props) {
    // useMemo locks the subscriber reference at the React layer regardless
    // of whether the factory below is module-cached, so the context value
    // can't accidentally become a re-render source if the factory's caching
    // strategy ever changes.
    const subscriber = useMemo(() => {
        const s = getRealtimeSubscriber();
        // BL-2342: an app relaunched in manual offline mode starts with its
        // transport suspended, so no socket or catch-up opens while native
        // detection settles. OfflineModeProvider resumes it if this turns out
        // to be a browser, or when the operator taps Go online. BUG-2053: the
        // typed client's first reads start from the same answer. BL-2343: an
        // offline document (served because a navigation failed) starts
        // suspended too, so an offline cold start opens no socket.
        if (startsInManualOffline() || readOfflineDocumentFlag()) s.setSuspended?.(true);
        return s;
    }, []);
    return (
        <RealtimeStack subscriber={subscriber}>
            {/* BL-2342 — the phone app's offline mode. Inside the stack because
                it reads the subscriber (to suspend it while offline) and the
                connection monitor; around the bar and the app because both
                read the mode. Inert in a browser. */}
            <OfflineModeProvider>
                {/* BL-2438 — the change queue's not-synced count: read inside the
                    signed-in shell, shown by the bar up here. */}
                <ChangeQueueCountBridge>
                    {/* BL-2491 — the live Inbox count: subscribed here, inside the
                        stack and the offline mode; its topic is minted inside the
                        signed-in shell (DashboardShell's reporter). */}
                    <InboxCountBridge>
                        {/* BL-1090 — the shared connection-health bar, mounted once for
                            the whole dashboard (inside the phone app, the offline
                            mode's own bar). Renders nothing, and occupies no layout,
                            while the connection is healthy. */}
                        <ConnectionHealthBarMount />
                        {children}
                    </InboxCountBridge>
                </ChangeQueueCountBridge>
            </OfflineModeProvider>
        </RealtimeStack>
    );
}
